import React from 'react';
import { Box, Paper, Typography, CircularProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { NotificationTrendData } from '../../../types/notification-admin';

interface EngagementChartProps {
  data: NotificationTrendData[];
  loading?: boolean;
  title?: string;
  showActions?: boolean;
}

/**
 * EngagementChart - Line/Area chart showing notification engagement trends
 * Displays sent, delivered, read, and actioned metrics over time
 */
const EngagementChart: React.FC<EngagementChartProps> = ({
  data,
  loading = false,
  title = 'Notification Engagement Trends',
  showActions = true,
}) => {
  const [chartType, setChartType] = React.useState<'line' | 'area'>('area');

  if (loading) {
    return (
      <Paper sx={{ p: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No trend data available
        </Typography>
      </Paper>
    );
  }

  // Format data for chart
  const chartData = data.map(item => ({
    date: format(item.date, 'MMM dd'),
    fullDate: format(item.date, 'PPP'),
    Sent: item.sent,
    Delivered: item.delivered,
    Read: item.read,
    Actioned: item.actioned,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2 }} elevation={3}>
          <Typography variant="subtitle2" gutterBottom>
            {payload[0]?.payload?.fullDate}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  const Chart = chartType === 'area' ? AreaChart : LineChart;
  const DataComponent = chartType === 'area' ? Area : Line;

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">{title}</Typography>

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, value) => value && setChartType(value)}
          size="small"
        >
          <ToggleButton value="line">Line</ToggleButton>
          <ToggleButton value="area">Area</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <Chart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            stroke="#888"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#888"
            style={{ fontSize: '12px' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '14px' }}
            iconType="line"
          />

          <DataComponent
            type="monotone"
            dataKey="Sent"
            stroke="#1976d2"
            fill="#1976d2"
            fillOpacity={chartType === 'area' ? 0.1 : undefined}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />

          <DataComponent
            type="monotone"
            dataKey="Delivered"
            stroke="#2e7d32"
            fill="#2e7d32"
            fillOpacity={chartType === 'area' ? 0.1 : undefined}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />

          <DataComponent
            type="monotone"
            dataKey="Read"
            stroke="#0288d1"
            fill="#0288d1"
            fillOpacity={chartType === 'area' ? 0.1 : undefined}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />

          {showActions && (
            <DataComponent
              type="monotone"
              dataKey="Actioned"
              stroke="#7b1fa2"
              fill="#7b1fa2"
              fillOpacity={chartType === 'area' ? 0.1 : undefined}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          )}
        </Chart>
      </ResponsiveContainer>

      {/* Summary Statistics */}
      <Box display="flex" justifyContent="space-around" mt={3} pt={2} borderTop="1px solid #e0e0e0">
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Total Sent
          </Typography>
          <Typography variant="h6">
            {chartData.reduce((sum, item) => sum + item.Sent, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Total Read
          </Typography>
          <Typography variant="h6">
            {chartData.reduce((sum, item) => sum + item.Read, 0).toLocaleString()}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography variant="caption" color="text.secondary">
            Avg. Read Rate
          </Typography>
          <Typography variant="h6">
            {(
              (chartData.reduce((sum, item) => sum + item.Read, 0) /
                chartData.reduce((sum, item) => sum + item.Sent, 0)) *
              100
            ).toFixed(1)}
            %
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default EngagementChart;
